/**
 * Household and family management types
 */

/**
 * Role types for household members
 */
export enum HouseholdRole {
  ADMINISTRATOR = "administrator",
  FAMILY_MEMBER = "family_member",
}

/**
 * Permissions for each role
 */
export interface RolePermissions {
  canViewAIInsights: boolean
  canManageMembers: boolean
  canInviteMembers: boolean
  canEditHouseholdSettings: boolean
  canDeleteHousehold: boolean
  canManageBudget: boolean
  canViewExpenses: boolean
  canAddExpenses: boolean
}

/**
 * Role permissions mapping
 */
export const ROLE_PERMISSIONS: Record<HouseholdRole, RolePermissions> = {
  [HouseholdRole.ADMINISTRATOR]: {
    canViewAIInsights: true,
    canManageMembers: true,
    canInviteMembers: true,
    canEditHouseholdSettings: true,
    canDeleteHousehold: true,
    canManageBudget: true,
    canViewExpenses: true,
    canAddExpenses: true,
  },
  [HouseholdRole.FAMILY_MEMBER]: {
    canViewAIInsights: true,
    canManageMembers: false,
    canInviteMembers: false,
    canEditHouseholdSettings: false,
    canDeleteHousehold: false,
    canManageBudget: false,
    canViewExpenses: true,
    canAddExpenses: true,
  },
}

/**
 * Individual household member
 */
export interface HouseholdMember {
  userId: string
  email: string
  displayName: string | null
  // The photo that came with the sign-in provider (Google/Apple).
  photoURL: string | null
  // An avatar the member uploaded themselves; takes precedence over photoURL.
  avatarImageId?: string | null
  role: HouseholdRole
  joinedAt: string // ISO date string
  addedBy: string // userId of who added/invited them
}

/**
 * Household entity
 */
export interface Household {
  id: string
  name: string
  description?: string
  createdBy: string // userId of creator
  createdAt: string // ISO date string
  updatedAt: string // ISO date string
  // Household photo (images row id) — set by an administrator.
  imageId?: string | null
  members: HouseholdMember[]
  settings?: HouseholdSettings
}

/**
 * Household settings/preferences
 */
export interface HouseholdSettings {
  currency?: string
  timezone?: string
  monthStartDay?: number // 1-31, for custom budget periods
  // Default way to split a new shared expense. Prefills the split control on
  // the expense form so couples set their arrangement once. Excludes "none"
  // (personal) — that's an explicit per-expense choice, not a default.
  defaultSplitMode?: SharedSplitMode
  // Per-category split rules, keyed by category name (the same value expenses
  // store in their `category` field). When an expense's category has a rule,
  // the form prefills that split — overridable per transaction. Categories
  // without a rule fall back to defaultSplitMode. Keyed by name rather than id
  // so a rule survives the category-id churn on context switches and can be
  // pre-set in the creation wizard before the household's categories exist.
  categorySplitRules?: Record<string, CategorySplitRule>
  // Per-member monthly "fun money" allowance (userId → kr/month). Personal
  // (unsplit) expenses logged in the household budget count against it —
  // guilt-free personal spending that stays visible but never enters the
  // settle-up math. 0/absent = no allowance set for that member.
  memberAllowances?: Record<string, number>
  // A fixed monthly amount one member pays another for living in a home they
  // don't own. Deliberately not a shared expense and not a settlement of one:
  // it buys no ownership and covers no specific bill, so splitting it or
  // categorising it would both misstate what it is. Settle-up accrues it per
  // elapsed month and nets it against everything else.
  housingContribution?: HousingContribution | null
}

/** The boligbidrag arrangement: who pays whom, how much, from when. */
export interface HousingContribution {
  // Payer and recipient, both household members. An arrangement naming
  // somebody who has left is ignored rather than reassigned.
  fromUserId: string
  toUserId: string
  amount: number
  /** First month it applies, "YYYY-MM-DD". Accrual starts in that month. */
  startDate: string
}

/** Split modes that represent a shared cost (everything except "personal"). */
export type SharedSplitMode = "equal" | "income" | "custom" | "exact"

export const DEFAULT_SPLIT_MODE: SharedSplitMode = "equal"

/**
 * Split modes that make sense as a blanket, set-once category rule. Excludes
 * "custom"/"exact" — those need per-transaction numbers, so they stay an
 * explicit per-expense choice rather than a category default.
 */
export type CategorySplitMode = "equal" | "income" | "single"

/** A split rule attached to a category. */
export interface CategorySplitRule {
  mode: CategorySplitMode
  // For "single": the member (userId) who owes the whole cost in this category.
  owner?: string
}

/** Currencies offered in the household settings picker. */
export const SUPPORTED_CURRENCIES = ["NOK", "SEK", "DKK", "EUR", "USD", "GBP"] as const

export const DEFAULT_HOUSEHOLD_CURRENCY = "NOK"

/**
 * Invitation status
 */
export enum InvitationStatus {
  PENDING = "pending",
  ACCEPTED = "accepted",
  DECLINED = "declined",
  EXPIRED = "expired",
}

/**
 * Household invitation
 */
export interface HouseholdInvitation {
  id: string
  householdId: string
  householdName: string
  invitedEmail: string
  invitedBy: string // userId
  invitedByName: string // display name
  role: HouseholdRole
  status: InvitationStatus
  createdAt: string // ISO date string
  expiresAt: string // ISO date string
  acceptedAt?: string // ISO date string
  declinedAt?: string // ISO date string
  // The magic-link token never reaches the client: the server stores only a
  // hash and the plaintext exists solely in the emailed link.
  emailSentAt?: string // ISO date string - when email was sent
  emailSendAttempts?: number // Number of email send attempts
  emailError?: string // Last email error message if any
  // Resend tracking fields
  lastResentAt?: string // ISO date string - when invitation was last resent
  resentBy?: string // userId of who resent the invitation
}

/**
 * User's household memberships summary
 */
export interface UserHouseholds {
  userId: string
  households: Array<{
    householdId: string
    householdName: string
    role: HouseholdRole
    memberCount: number
  }>
  householdIds: string[] // Simple array for Firestore security rules
  pendingInvitations: HouseholdInvitation[]
}

/**
 * Budget context - determines if we're viewing personal or household budget
 */
export enum BudgetContext {
  PERSONAL = "personal",
  HOUSEHOLD = "household",
}

/**
 * Active budget context with household info if applicable
 */
export interface ActiveBudgetContext {
  context: BudgetContext
  householdId?: string
  householdName?: string
  userRole?: HouseholdRole
}
